/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, ElementRef, Inject, Input, NgZone, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import 'moment-timezone';
import * as Highcharts from 'highcharts';

import template from './ext-dem-performance-chart.component.html';

import {Downloader} from '../../../components/util/downloader';
import {StatsResource} from '../../../components/api/resources/resource/stats-resources/stats-resource';
import {CHART_COLORS} from '../options/chart-colors';
import {ADN_CHART_COUNT_TYPES, ADN_CHART_Y_AXIS_OPTIONS} from '../options/chart-options';
import {AdnLogService} from '../../../components/util/adn-log.service';
import {AppLocaleService} from '../locale/app-locale.service';
import {getIntlLocaleData} from '../locale/intl-locale-data';
import {applyHighchartsLocale} from '../../workspace/widget/workspace-widget-chart-commons';
import {UserProfileResource} from '../../../components/api/resources/resource/user-profile.resource';
import {ChartOptionsUtil} from '../options/chart-options-util.service';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserProfile} from '../../../components/session/local-user-profile';

@Component({
  standalone: false,
  selector: 'adn-ext-dem-performance-chart',
  template
})
export class ExtDemPerformanceChartComponent implements OnInit, OnChanges, OnDestroy {
  @Input() obj: any;
  @Input() adnChartType: string;
  @Input() chartType: string;
  @Input() absChunkMin: string;
  @Input() biddersHook: any;
  @Input() statsData: any;
  @Input() bidder: string;

  @ViewChild('chartContainer')
  set chartContainer(chartContainer: ElementRef<HTMLElement>) {
    this.chartContainerRef = chartContainer;
    this.renderChart();
  }

  isCollection = false;
  dateOption: any;
  startDate: any;
  endDate: any;
  countType = ADN_CHART_COUNT_TYPES.singular;
  yAxisOption = ADN_CHART_Y_AXIS_OPTIONS.multiple;
  selFieldIds: any;
  selFieldId: any;
  haveStats = false;
  error: any = false;
  isPending = false;
  noData = false;
  totals: any;
  canceller: any;

  private chart: Highcharts.Chart | null = null;
  private chartContainerRef: ElementRef<HTMLElement> | null = null;
  private chartOptionsConfig: any;
  private chartOptions: any;
  private series: any[] = [];
  private localUser: any;
  private profile: any;
  private networkTimezoneOffset = 0;
  private initialized = false;
  private resolvedStatsData: any;

  get resolvedChartType(): string {
    return this.adnChartType || this.chartType;
  }

  get resolvedBiddersHook(): any {
    return this.biddersHook;
  }

  constructor(
    private readonly statsResource: StatsResource,
    private readonly ngZone: NgZone,
    private readonly appLocale: AppLocaleService,
    private readonly translate: TranslateService,
    private readonly adnLog: AdnLogService,
    @Inject(LocalUserProfile) private readonly localUserProfile: any,
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    @Inject(ChartOptionsUtil) private readonly ChartOptionsUtil: any,
    @Inject(UserProfileResource) private readonly UserProfile: any
  ) {
  }

  ngOnInit(): void {
    this.resolvedStatsData = this.statsData;
    this.initProfileAndChart();
    this.wireBiddersHook();
    this.initialized = true;
    if (this.chartOptionsConfig) {
      this.startStatsGet();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.initialized) {
      return;
    }

    if (changes.statsData) {
      this.resolvedStatsData = this.statsData;
    }

    if (changes.adnChartType || changes.chartType) {
      this.initProfileAndChart();
      this.wireBiddersHook();
      if (this.chartOptionsConfig) {
        this.startStatsGet();
      }
      return;
    }

    if (changes.obj || changes.bidder || changes.statsData || changes.biddersHook) {
      this.isCollection = _.isArray(this.obj);
      this.series = this.getSelFields();
      this.wireBiddersHook();
      if (this.chartOptionsConfig) {
        this.startStatsGet();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.canceller) {
      this.canceller.resolve('Destroyed component');
    }
    this.destroyChart();
  }

  changeCallback(
    dateOptionId: string,
    startDate: any,
    endDate: any,
    countTypeId: string,
    statTypeId: string,
    yAxisOptionId: string,
    selFieldIds: any,
    selFieldId: any
  ): void {
    void statTypeId;

    this.dateOption = this.ChartOptionsUtil.getDateOptions()[dateOptionId];
    this.startDate = moment(startDate);
    this.endDate = moment(endDate);
    this.countType = ADN_CHART_COUNT_TYPES[countTypeId];
    this.selFieldIds = selFieldIds;
    this.selFieldId = selFieldId;
    this.yAxisOption = ADN_CHART_Y_AXIS_OPTIONS[yAxisOptionId];
    this.series = this.getSelFields();
    this.startStatsGet();

    const perfKey = this.chartOptionsConfig.perfChartOptions;
    this.profile[perfKey] = {
      dateOptionId: _.get(this.dateOption, 'id'),
      startDate: this.startDate.toDate(),
      endDate: this.endDate.toDate(),
      yAxisOptionId: this.yAxisOption.id,
      countTypeId: this.countType.id,
      selFieldIds: this.selFieldIds,
      selFieldId: this.selFieldId
    };

    this.UserProfile.save({
      id: this.localUser.userId,
      profile: _.pick(this.profile, perfKey)
    }, (apiUser: any) => {
      this.localUser = this.localUserProfile.safeSaveProfile(perfKey, apiUser.profile[perfKey]);
      this.profile = this.localUser.profile;
    });
  }

  save(): void {
    const allDataFields = this.getSelFields();
    const headingFields = _.map(allDataFields, 'name');
    const fields = _.map(allDataFields, 'id');

    const rows: any[] = [];
    const refData = _.cloneDeep(this.series[0].data);
    for (let i = 0; i < refData.length; i++) {
      const dateData: any = {};
      for (let j = 0; j < this.series.length; j++) {
        if (j === 0) {
          dateData.date = moment(this.series[j].data[i].x).toString();
        }
        dateData[this.series[j].id] = this.series[j].data[i].y;
      }
      rows.push(dateData);
    }

    headingFields.unshift('Date');
    fields.unshift('date');

    const date = this.dateOption ? this.translate.instant('chart.dateOption.' + this.dateOption.id) : 'custom dates';
    const defaultName = (this.isCollection ? _.map(this.obj, ['name']).join('-') : this.obj.name + ' ' + date + '.xls').replace(/ /g, '-');
    new Downloader().saveXls2(defaultName, headingFields, fields, rows, []);
  }

  private wireBiddersHook(): void {
    const hook = this.resolvedBiddersHook;
    if (!hook) {
      return;
    }
    hook.reboot = (
      currentStatsData: any,
      dateOption: any,
      startDate: any,
      endDate: any,
      countType: any,
      yAxisOption: any,
      selFieldIds: any,
      selFieldId: any
    ) => {
      this.resolvedStatsData = currentStatsData;
      this.dateOption = dateOption;
      this.startDate = startDate;
      this.endDate = endDate;
      this.countType = countType;
      this.selFieldIds = selFieldIds;
      this.selFieldId = selFieldId;
      this.yAxisOption = yAxisOption;
      this.series = this.getSelFields();
      this.startStatsGet();
    };
  }

  private initProfileAndChart(): void {
    if (!this.resolvedChartType) {
      return;
    }

    this.isCollection = _.isArray(this.obj);
    this.chartOptionsConfig = this.ChartOptionsUtil.getOptions(this.resolvedChartType);
    if (!this.chartOptionsConfig) {
      return;
    }

    this.localUser = this.localUserProfile.get();
    this.profile = this.localUser.profile;

    const perfKey = this.chartOptionsConfig.perfChartOptions;
    const profileStartDate = _.get(this.profile[perfKey], 'startDate');
    const profileEndDate = _.get(this.profile[perfKey], 'endDate');
    const profileCountType = ADN_CHART_COUNT_TYPES[_.get(this.profile[perfKey], 'countTypeId')];
    const profileYAxisOption = ADN_CHART_Y_AXIS_OPTIONS[_.get(this.profile[perfKey], 'yAxisOptionId')];
    const profileSelFieldIds = _.get(this.profile[perfKey], 'selFieldIds');
    const profileSelFieldId = _.get(this.profile[perfKey], 'selFieldId');

    this.dateOption = this.ChartOptionsUtil.getDateOptions()[_.get(this.profile[perfKey], 'dateOptionId')];
    if (!this.dateOption && (!profileStartDate || !profileEndDate)) {
      this.dateOption = this.ChartOptionsUtil.getDateOptions().last12Hours;
    }
    this.startDate = this.dateOption ? this.dateOption.startDate() : moment(profileStartDate);
    this.endDate = this.dateOption ? this.dateOption.endDate() : moment(profileEndDate);
    this.countType = profileCountType ? profileCountType : ADN_CHART_COUNT_TYPES.singular;
    this.yAxisOption = profileYAxisOption ? profileYAxisOption : ADN_CHART_Y_AXIS_OPTIONS.multiple;
    this.selFieldIds = profileSelFieldIds ? profileSelFieldIds : _.map(this.chartOptionsConfig.fields, 'id')[0];
    this.selFieldId = profileSelFieldId ? profileSelFieldId : _.map(this.chartOptionsConfig.fields, 'id')[0];

    const timezone = this.LocalNetworkProfile.get('timeZone');
    this.networkTimezoneOffset = moment.tz(timezone).utcOffset();

    this.series = this.getSelFields();
    this.chartOptions = this.createChartConfig('line', this.series, this.getYAxes(), 300);
  }

  private getSelFields(): any[] {
    if (this.isCollection) {
      return _.map(this.obj, (c: any) => {
        return {
          data: [],
          id: c.bidder,
          name: c.bidder,
          yAxis: 0
        };
      });
    }
    const map = _.filter(_.cloneDeep(this.chartOptionsConfig.fields), (value: any) => {
      return this.selFieldIds.indexOf(value.id) > -1 && value.id !== 'customFields';
    });
    if (this.yAxisOption === ADN_CHART_Y_AXIS_OPTIONS.shared) {
      _.forEach(map, (c: any) => {
        c.yAxis = 0;
      });
    }
    return map;
  }

  private createChartConfig(type: string, series: any[], yAxisData: any, height: number): any {
    return {
      chart: {
        type,
        zoomType: 'x',
        showAxes: true,
        height
      },
      plotOptions: {
        series: {
          marker: {
            enabled: false
          },
          turboThreshold: 2000
        }
      },
      tooltip: {
        shared: true
      },
      xAxis: {
        type: 'datetime',
        title: {
          enabled: false,
          text: 'Date and Time'
        }
      },
      yAxis: yAxisData,
      series,
      title: {
        text: null
      },
      credits: {
        enabled: false
      },
      accessibility: {
        enabled: false
      },
      exporting: {
        enabled: true
      }
    };
  }

  private getYAxes(extras?: any[]): any {
    if (this.isCollection) {
      return {
        title: {text: null},
        min: 0,
        floor: 0,
        opposite: false,
        id: 0
      };
    }
    const standardFields = _.filter(_.map(this.chartOptionsConfig.fields, (yAxis: any) => {
      const theColor = CHART_COLORS[yAxis.yAxis];
      return {
        title: {text: null},
        min: 0,
        opposite: false,
        allowDecimals: this.yAxisOption === ADN_CHART_Y_AXIS_OPTIONS.shared ? true : !!yAxis.allowDecimals,
        id: yAxis.yAxis,
        labels: {style: {color: theColor}}
      };
    }), (f: any) => f.id > -1);
    _.forEach(extras, (f: any) => {
      const theColor = CHART_COLORS[CHART_COLORS.length - 1];
      standardFields.push({
        title: {text: null},
        min: 0,
        opposite: false,
        allowDecimals: false,
        name: f,
        labels: {style: {color: theColor}},
        id: standardFields.length
      });
    });
    return standardFields;
  }

  private chunkYValue(yval: any): any {
    if (_.has(yval, 'amount')) {
      return yval.amount;
    } else if (_.has(yval, 'count')) {
      return yval.count;
    }
    return yval;
  }

  private getCollDataPoint(theChunks: any, fieldId: string, bidderId?: string): any {
    let objParam = 'creative';
    if (this.resolvedChartType === 'LINE_ITEM') {
      objParam = 'lineItem';
    } else if (this.resolvedChartType === 'AD_UNIT') {
      objParam = 'adUnit';
    }

    if (this.resolvedChartType === 'EXTERNAL_DEMAND_SOURCE') {
      return _.reduce(_.map(_.filter(theChunks, (c: any) => {
        return bidderId ? c.bidder === bidderId : true;
      }), (m: any) => {
        return m[fieldId];
      }), (sum: number, n: any) => {
        return sum + this.chunkYValue(n);
      }, 0);
    }

    return _.get(_.find(theChunks, (c: any) => {
      return c[objParam].id === fieldId;
    }), this.selFieldId) || 0;
  }

  private startStatsGet(): void {
    if (!this.chartOptionsConfig) {
      return;
    }

    const chunkSize = this.ChartOptionsUtil.getChunkSize(this.startDate, this.endDate, this.absChunkMin);
    const statsParams: Record<string, any> = {
      id: this.isCollection ? null : this.obj.id,
      idKey: this.chartOptionsConfig.idKey,
      startDate: this.startDate,
      endDate: this.endDate,
      groupBy: chunkSize.key
    };

    if (this.isCollection) {
      const field = _.find(this.chartOptionsConfig.fields, ['id', this.selFieldId]);
      _.forEach(this.series, (series: any) => {
        series.isPercentage = field.isPercentage;
        series.allowDecimals = field.allowDecimals;
        series.tooltip = field.tooltip;
      });
    }

    const statsHandling = (stats: any) => {
      applyHighchartsLocale(getIntlLocaleData(this.appLocale.getLocaleId()), this.networkTimezoneOffset);
      this.error = false;

      const allSeries = this.series;
      const allBidders = _.uniq(_.flatten(_.map(stats.chunks, (chunk: any) => {
        return _.uniq(_.map(chunk.chunks, (subChunk: any) => {
          return subChunk.bidder;
        }));
      })));

      const hook = this.resolvedBiddersHook;
      if (!this.isCollection) {
        if (hook && _.isFunction(hook.onBiddersFound) && !hook.biddersRegistered) {
          hook.onBiddersFound(allBidders, stats);
        }
        if (this.haveStats && hook && _.isFunction(hook.onSelections)) {
          hook.onSelections(stats, this.dateOption, this.startDate, this.endDate, this.countType, this.yAxisOption, this.selFieldIds, this.selFieldId);
        }
      }

      const allTotals: any = {};
      const keyedChunks = _.keyBy(stats.chunks, 'chunkStart');
      const yAxis = this.getYAxes();
      _.forEach(this.isCollection ? allSeries : this.chartOptionsConfig.fields, (lineOnChart: any, loopCounter: number) => {
        const series = this.isCollection ? lineOnChart : _.find(allSeries, (aSeries: any) => {
          return aSeries.id === lineOnChart.id;
        });

        if (series) {
          if (this.isCollection || this.yAxisOption === ADN_CHART_Y_AXIS_OPTIONS.shared) {
            series.yAxis = 0;
          } else {
            series.yAxis = _.get(this.chartOptionsConfig.fields[series.id], 'yAxis') || series.yAxis;
          }

          const yAxisFieldId = _.get(this.chartOptionsConfig.fields[series.id], 'yAxis');
          const yAxisFieldColor = _.get(yAxis[yAxisFieldId], 'labels.style.color');
          series.color = this.isCollection ? CHART_COLORS[loopCounter % CHART_COLORS.length] : yAxisFieldColor || CHART_COLORS[CHART_COLORS.length - 1];
        }

        let runningTotal = 0;
        let maxTotal = -1;
        const data = _.map(stats.chunkRange, (statsDate: any) => {
          let dataPoint = null;
          if (keyedChunks[statsDate]) {
            const dataReference = lineOnChart.toTheMax && !this.bidder ? [keyedChunks[statsDate].totals] : keyedChunks[statsDate].chunks;
            dataPoint = this.isCollection
              ? this.getCollDataPoint(dataReference, this.selFieldId, series.id)
              : this.getCollDataPoint(dataReference, lineOnChart.id, this.bidder);
          }
          let value = dataPoint || 0;
          if (lineOnChart.isPercentage) {
            value = value * 100;
            runningTotal = value;
          } else if (lineOnChart.toTheMax) {
            runningTotal = value;
            maxTotal = maxTotal > runningTotal ? maxTotal : runningTotal;
          } else {
            runningTotal += value;
          }
          return {
            x: moment(statsDate).valueOf(),
            y: this.countType === ADN_CHART_COUNT_TYPES.cumulative && !lineOnChart.singularOnly ? runningTotal : value
          };
        });
        allTotals[lineOnChart.id] = maxTotal > -1 ? maxTotal : runningTotal;
        if (series) {
          series.data = data;
        }
      });

      if (this.bidder) {
        this.totals = allTotals;
      } else {
        this.totals = stats.totals || stats;
      }

      this.haveStats = true;
      this.noData = !this.series.length;
      this.isPending = false;
      this.chartOptions = this.createChartConfig('line', this.series, yAxis, 300);
      this.renderChart();
    };

    if (this.resolvedStatsData) {
      statsHandling(this.resolvedStatsData);
    } else {
      this.statsResource.get(statsParams, (stats: any) => {
        this.ngZone.run(() => {
          statsHandling(stats);
        });
      }, (data: any) => {
        this.ngZone.run(() => {
          this.adnLog.warn(data);
          if (data.status !== -1) {
            this.error = true;
          }
          this.isPending = false;
        });
      }, this);
    }
  }

  private renderChart(): void {
    if (!this.chartContainerRef?.nativeElement || !this.chartOptions) {
      return;
    }

    this.destroyChart();
    this.chart = Highcharts.chart(this.chartContainerRef.nativeElement, this.chartOptions);
  }

  private destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }
}
